import type {
  ColDef,
  IDoesFilterPassParams,
  IFilterComp,
  IFilterParams,
} from 'ag-grid-community';

type CheckboxFilterModel = {
  values: string[];
};

type CheckboxFilterParams = IFilterParams & {
  colDef: ColDef;
  availableValues?: string[]; // Optional: if provided, use these instead of scanning grid
  // Value mapper: maps API values to display values (e.g., "True" -> "Yes", "False" -> "No")
  valueMapper?: (apiValue: string) => string;
  // Value extractor: extracts a part from values (e.g., extracts year from "1999-01-23")
  valueExtractor?: (apiValue: string) => string;
  // Reverse mapper: maps display values back to API values (e.g., "Yes" -> "True", "No" -> "False")
  reverseMapper?: (displayValue: string) => string;
  // If true, skip client-side filtering (server handles filtering)
  serverSideFiltering?: boolean;
  // If true, deduplicate values case-insensitively, keeping the first occurrence
  caseInsensitiveDeduplication?: boolean;
};

const BLANK_KEY = '__AG_CHECKBOX_FILTER_BLANK__';

// Helper function to deduplicate values case-insensitively, keeping the first occurrence
function deduplicateCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const lower = value.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      result.push(value);
    }
  }

  return result;
}

export class AgCheckboxFilter implements IFilterComp<CheckboxFilterModel> {
  private params!: CheckboxFilterParams;

  private gui!: HTMLElement;

  private selectedContainer!: HTMLElement;

  private listContainer!: HTMLElement;

  private searchInput!: HTMLInputElement;

  private resetButton!: HTMLButtonElement;

  private values: string[] = [];

  private filteredValues: string[] = [];

  private selectedValues: Set<string> = new Set();

  public init(params: CheckboxFilterParams): void {
    this.params = params;
    this.gui = document.createElement('div');
    this.gui.className =
      'ag-checkbox-filter tw-flex tw-flex-col tw-gap-2 tw-p-2 tw-text-sm';
    this.gui.style.maxWidth = '250px';

    this.createSearchInput();
    this.createSelectedContainer();
    this.createResetButton();
    this.createListContainer();

    this.updateAvailableValues();
  }

  public getGui(): HTMLElement {
    return this.gui;
  }

  public doesFilterPass(params: IDoesFilterPassParams): boolean {
    // If server-side filtering is enabled, always return true (server handles filtering)
    if (this.params.serverSideFiltering) {
      return true;
    }

    // If no values are selected, show all rows (filter is inactive)
    if (!this.isFilterActive()) {
      return true;
    }

    // Skip filtering for detail rows - they should always show if their parent passes
    if (params.node.data && (params.node.data as any).kind === 'detail') {
      return true;
    }

    const value = this.getValue(params);

    // Handle null/undefined values
    if (value === null || value === undefined) {
      return this.selectedValues.has(BLANK_KEY);
    }

    // Check if value is an array (for fields like contributions that are arrays)
    if (Array.isArray(value)) {
      // For arrays, check if any item in the array matches any selected value
      return value.some((item) => {
        const normalized = this.normalizeValue(item);
        return normalized !== null && this.selectedValues.has(normalized);
      });
    }

    // Handle comma-separated strings (like "Producer, Writer")
    const normalized = this.normalizeValue(value);
    if (normalized === null) {
      return this.selectedValues.has(BLANK_KEY);
    }

    // For comma-separated strings, split and check if any part matches
    // This handles fields like contributions that are stored as "Producer, Writer"
    if (normalized.includes(', ')) {
      const parts = normalized.split(', ').map((part) => part.trim());
      return parts.some((part) => this.selectedValues.has(part));
    }

    // For single values, check direct match
    return this.selectedValues.has(normalized);
  }

  public isFilterActive(): boolean {
    // Filter is active only when at least one value is selected
    return this.selectedValues.size > 0;
  }

  public getModel(): CheckboxFilterModel | null {
    if (!this.isFilterActive()) {
      return null;
    }

    // If reverse mapper is provided, map display values back to API values
    let values = Array.from(this.selectedValues);
    if (this.params.reverseMapper) {
      values = values.map((v) => this.params.reverseMapper!(v));
    }

    return {
      values,
    };
  }

  public setModel(model: CheckboxFilterModel | null): void {
    if (!model) {
      // Clear selection when model is null (no filter)
      this.selectedValues.clear();
      this.renderOptions();
      return;
    }

    this.selectedValues = new Set(model.values);
    this.renderOptions();
  }

  public afterGuiAttached(): void {
    this.searchInput?.focus();
  }

  public onNewRowsLoaded(): void {
    this.updateAvailableValues();
  }

  public destroy(): void {
    // no-op
  }

  public refresh(): boolean {
    this.updateAvailableValues();
    return true;
  }

  // Method to update available values from external source (e.g., API)
  public setAvailableValues(values: string[]): void {
    // Apply value extractor if provided (e.g., extract year from dates)
    let processedValues = values;
    if (this.params.valueExtractor) {
      processedValues = values.map((v) => this.params.valueExtractor!(v));
    }

    // Apply value mapper if provided (e.g., "True" -> "Yes", "False" -> "No")
    if (this.params.valueMapper) {
      processedValues = processedValues.map((v) => this.params.valueMapper!(v));
    }

    // Remove duplicates after processing
    let deduplicatedValues = Array.from(new Set(processedValues));

    // Apply case-insensitive deduplication if enabled
    if (this.params.caseInsensitiveDeduplication) {
      deduplicatedValues = deduplicateCaseInsensitive(deduplicatedValues);
    }

    this.values = deduplicatedValues;
    this.values.sort((a, b) => a.localeCompare(b));
    this.filteredValues = [...this.values];

    // Preserve selected values that still exist
    const current = new Set(this.selectedValues);
    this.selectedValues = new Set(
      this.values.filter((value) => current.has(value))
    );

    this.renderOptions();
  }

  private createSearchInput() {
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = 'Search';
    this.searchInput.className =
      'tw-rounded tw-bg-[#1d2023] tw-border tw-border-[#30353b] tw-px-2 tw-py-1';
    this.searchInput.addEventListener('input', () => {
      this.applySearch(this.searchInput.value);
    });
    this.gui.appendChild(this.searchInput);
  }

  private createSelectedContainer() {
    this.selectedContainer = document.createElement('div');
    this.selectedContainer.className =
      'tw-flex tw-flex-col tw-gap-1 tw-pb-2 tw-border-b tw-border-[#30353b] tw-max-h-32 tw-overflow-auto';
    this.gui.appendChild(this.selectedContainer);
  }

  private createResetButton() {
    const resetContainer = document.createElement('div');
    resetContainer.className = 'tw-flex tw-justify-end tw-pb-2';

    this.resetButton = document.createElement('button');
    this.resetButton.type = 'button';
    this.resetButton.textContent = 'Reset';
    this.resetButton.className =
      'tw-text-xs tw-text-blue-400 hover:tw-text-blue-300 tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0 tw-px-1';
    this.resetButton.style.display = 'none';
    this.resetButton.addEventListener('click', () => {
      this.selectedValues.clear();
      this.params.filterChangedCallback();
      this.renderOptions();
    });

    resetContainer.appendChild(this.resetButton);
    this.gui.appendChild(resetContainer);
  }

  private createListContainer() {
    this.listContainer = document.createElement('div');
    this.listContainer.className =
      'tw-max-h-60 tw-overflow-auto tw-flex tw-flex-col tw-gap-1';
    this.gui.appendChild(this.listContainer);
  }

  private updateAvailableValues() {
    // If availableValues are provided via params, use those instead of scanning grid
    if (this.params.availableValues && this.params.availableValues.length > 0) {
      let processedValues = [...this.params.availableValues];

      // Apply value extractor if provided (e.g., extract year from dates)
      if (this.params.valueExtractor) {
        processedValues = processedValues.map((v) =>
          this.params.valueExtractor!(v)
        );
      }

      // Apply value mapper if provided (e.g., "True" -> "Yes", "False" -> "No")
      if (this.params.valueMapper) {
        processedValues = processedValues.map((v) =>
          this.params.valueMapper!(v)
        );
      }

      // Remove duplicates after processing
      let deduplicatedValues = Array.from(new Set(processedValues));

      // Apply case-insensitive deduplication if enabled
      if (this.params.caseInsensitiveDeduplication) {
        deduplicatedValues = deduplicateCaseInsensitive(deduplicatedValues);
      }

      this.values = deduplicatedValues;
      this.values.sort((a, b) => a.localeCompare(b));
      this.filteredValues = [...this.values];
    } else {
      // Fallback to scanning grid (original behavior)
      const valuesMap = new Map<string, string>(); // Map<lowercase, first occurrence>

      this.params.api.forEachNode((node) => {
        if (!node.data || (node.data as any).kind === 'detail') {
          return;
        }

        const value = this.getValue({ node } as IDoesFilterPassParams);
        let normalized = this.normalizeValue(value);

        // Apply value extractor if provided
        if (normalized && this.params.valueExtractor) {
          normalized = this.params.valueExtractor(normalized);
        }

        // Apply value mapper if provided
        if (normalized && this.params.valueMapper) {
          normalized = this.params.valueMapper(normalized);
        }

        const finalValue = normalized ?? BLANK_KEY;

        if (this.params.caseInsensitiveDeduplication) {
          const lower = finalValue.toLowerCase();
          if (!valuesMap.has(lower)) {
            valuesMap.set(lower, finalValue);
          }
        } else {
          valuesMap.set(finalValue, finalValue);
        }
      });

      this.values = Array.from(valuesMap.values());
      this.values.sort((a, b) => a.localeCompare(b));
      this.filteredValues = [...this.values];
    }

    // Preserve selected values that still exist in the new values list
    const current = new Set(this.selectedValues);
    this.selectedValues = new Set(
      this.values.filter((value) => current.has(value))
    );

    this.renderOptions();
  }

  private renderOptions() {
    // Clear containers
    this.selectedContainer.innerHTML = '';
    this.listContainer.innerHTML = '';

    // Separate filtered values into selected and unselected
    const selected: string[] = [];
    const unselected: string[] = [];

    this.filteredValues.forEach((value) => {
      if (this.selectedValues.has(value)) {
        selected.push(value);
      } else {
        unselected.push(value);
      }
    });

    // Sort selected values
    selected.sort((a, b) => a.localeCompare(b));

    // Render selected values at the top with a label if there are any
    if (selected.length > 0) {
      const labelDiv = document.createElement('div');
      labelDiv.className =
        'tw-text-xs tw-text-neutral-400 tw-font-medium tw-mb-1';
      labelDiv.textContent = 'Selected:';
      this.selectedContainer.appendChild(labelDiv);

      selected.forEach((value) => {
        const label = this.createCheckboxLabel(value, true);
        this.selectedContainer.appendChild(label);
      });
    }

    // Render unselected values below
    unselected.forEach((value) => {
      const label = this.createCheckboxLabel(value, false);
      this.listContainer.appendChild(label);
    });

    // Show/hide reset button based on whether there are selected values
    if (this.resetButton) {
      this.resetButton.style.display =
        this.selectedValues.size > 0 ? 'block' : 'none';
    }
  }

  private createCheckboxLabel(value: string, isSelected: boolean): HTMLElement {
    const label = document.createElement('label');
    label.className =
      'tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-white tw-cursor-pointer tw-min-w-0';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'tw-h-4 tw-w-4';
    checkbox.checked = isSelected;
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        this.selectedValues.add(value);
      } else {
        this.selectedValues.delete(value);
      }
      this.params.filterChangedCallback();
      this.renderOptions();
    });

    const formatted = value === BLANK_KEY ? 'Blanks' : value;
    const optionLabel = document.createElement('span');
    optionLabel.textContent = formatted;
    optionLabel.className = 'tw-truncate tw-min-w-0 tw-flex-1';

    label.appendChild(checkbox);
    label.appendChild(optionLabel);

    return label;
  }

  private applySearch(value: string) {
    const query = value.toLowerCase();
    this.filteredValues = this.values.filter((option) => {
      const formatted = option === BLANK_KEY ? 'Blanks' : option;
      return formatted.toLowerCase().includes(query);
    });
    this.renderOptions();
  }

  private getValue(params: IDoesFilterPassParams): any {
    const { colDef } = this.params;
    const valueGetter = colDef.filterValueGetter || colDef.valueGetter;

    if (valueGetter && typeof valueGetter === 'function') {
      return valueGetter({
        api: this.params.api,
        column: this.params.column,
        context: this.params.context,
        data: params.node.data,
        node: params.node,
      } as any);
    }

    if (colDef.field && params.node.data) {
      return (params.node.data as any)[colDef.field];
    }

    return null;
  }

  private normalizeValue(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeValue(item) ?? '').join(', ');
    }

    return String(value);
  }
}
